import { render } from 'preact';

import { namespace } from '../helpers/namespaces';
import { hexToRGB } from '../helpers/colours';
import { applyThemeFromEmbedConfig, parseThemeConfig } from './theme-config';
import { getScorePredictorData } from './apiRequests';
// import Prediction from './prediction.component';

import ScorePredictor from './scorePredictor.component';

// import { DRAFT_SCORE_PREDICTOR } from './mockPredictor';

import { determineTenant, getClientHost } from '../helpers/client';
import { I18nextProvider } from 'react-i18next';
import { DataCaptureForm as Form } from 'playbook-data-capture-sdk-web';
import i18nInstance from '../i18n';

import { getAuthHeader } from '../helpers/authentication';

const ID_DATA_ATTRIBUTE = `data-${namespace}score-predictor`;
const ID_DATA_CAPTURE_FORM = `data-${namespace}form`;
const PREVIEW_DATA_ATTRIBUTE = `${ID_DATA_ATTRIBUTE}-preview`;
const TENANT_DATA_ATTRIBUTE = `data-${namespace}tenant`;
const INIT_ATTR = 'data-fek-initialised';
const NO_INIT_SELECTOR = `:not([${INIT_ATTR}])`;

/**
 * Class which goes through page and finds FEK predictions to embed
 */
export default class Predictions {
    constructor() {
        this.init();
        this.observeDOM();
    }

    init() {
        document
            .querySelectorAll(`[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`)
            .forEach(this.initNewScorePredictor.bind(this));
    }

    initNewScorePredictor(scorePredictorEmbed) {
        const embedTenant = scorePredictorEmbed.getAttribute(
            TENANT_DATA_ATTRIBUTE
        );
        determineTenant(embedTenant);

        let scorePredictorId =
            scorePredictorEmbed.getAttribute(ID_DATA_ATTRIBUTE);
        scorePredictorEmbed.setAttribute(INIT_ATTR, '');

        const matchId = scorePredictorEmbed.dataset.fekMatchId;

        const handleScorePredictor = (id) => {
            if (!id) {
                return;
            }

            getScorePredictorData(id).then((scorePredictorData) => {
                if (!scorePredictorData) {
                    return;
                }
                const authHeader = getAuthHeader();
                if (scorePredictorData?.gated && authHeader) {
                    getScorePredictorData(id, true, authHeader).then(
                        (gatedScorePredictorData) => {
                            if (!gatedScorePredictorData) {
                                return;
                            }
                            this.renderEmbed(
                                scorePredictorEmbed,
                                gatedScorePredictorData,
                                false,
                                null
                            );
                        }
                    );
                } else {
                    this.renderEmbed(
                        scorePredictorEmbed,
                        scorePredictorData,
                        false,
                        null
                    );
                }
            });
        };

        if (scorePredictorId === 'dynamic' && matchId) {
            this.getScorePredictorIdByMatchId(matchId)
                .then((data) => {
                    if (data && data.score_predictor) {
                        handleScorePredictor(data.score_predictor);
                    } else {
                        console.warn(
                            'Playbook Score Predictor embed: No score predictor found for match ID',
                            matchId
                        );
                    }
                })
                .catch((error) => {
                    console.warn('Playbook Score Predictor embed error', error);
                });
        } else {
            handleScorePredictor(scorePredictorId);
        }
    }

    /**
     * Function that fetches the score predictor ID by match ID
     *
     * @param {string|number} matchId the match ID
     * @returns {Promise<object|undefined>} Returns a promise that resolves to the match data object or undefined if an error occurs.
     */
    getScorePredictorIdByMatchId(matchId) {
        const endpoint = `https://${getClientHost()}${
            process.env.API_SDP_ADAPTOR_API
        }/matches/${matchId}`;

        return fetch(endpoint)
            .then((response) => response.json())
            .catch((error) => {
                console.warn(
                    'Playbook score predictor embed error fetching match data',
                    error
                );
            });
    }

    initPreviews(onPreviewEditChange) {
        document
            .querySelectorAll(`[${PREVIEW_DATA_ATTRIBUTE}]`)
            .forEach((previewEmbed) => {
                const data = JSON.parse(
                    previewEmbed.getAttribute(PREVIEW_DATA_ATTRIBUTE)
                );
                if (data?.prediction_id) {
                    this.renderEmbed(
                        previewEmbed,
                        data,
                        true,
                        onPreviewEditChange
                    );
                }
            });
    }

    /**
     * Sets up a MutationObserver to check the DOM
     * if new score predictor embeds have been dynamically added
     */
    observeDOM() {
        const observer = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const scorePredictorEmbed = node.querySelector(
                            `[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`
                        );
                        if (scorePredictorEmbed) {
                            this.initNewScorePredictor(scorePredictorEmbed);
                        }
                    }
                });
            });
        });

        observer.observe(document.body, { subtree: true, childList: true });
    }

    /**
     * Renders embeds to DOM
     *
     * @param {Element} container - embed container
     * @param {object} scorePredictorData - scorePredictorData data
     * @param {boolean} isPreview - poll embed is a preview
     * @param {Function} onPreviewEditChange callback function when contenteditable data changes
     */
    async renderEmbed(
        container,
        scorePredictorData,
        isPreview,
        onPreviewEditChange
    ) {
        // Check if there is a linked form
        const embeddedFormId =
            container.firstElementChild?.getAttribute(ID_DATA_CAPTURE_FORM);

        let embeddedFormData;

        if (embeddedFormId) {
            embeddedFormData = await Form.initEmbeddableNewForm(embeddedFormId)
                .then((formData) => formData)
                .catch((rejectValue) => rejectValue);
        }

        // Apply optional per-embed theme before render.
        applyThemeFromEmbedConfig(container, parseThemeConfig(container));

        // Set BG colour and gradient overrides.
        const hexBgColour = scorePredictorData?.background_colour;
        const rgbBgColour = hexBgColour ? hexToRGB(hexBgColour) : null;

        const colourOverrides = {
            hex: hexBgColour,
            rgb: rgbBgColour
        };

        const shadow =
            container.shadowRoot || container.attachShadow({ mode: 'open' });
        render(
            <I18nextProvider i18n={i18nInstance}>
                <ScorePredictor
                    predictionData={scorePredictorData}
                    embeddedFormData={embeddedFormData}
                    isPreview={isPreview}
                    container={container}
                    onPreviewEditChange={onPreviewEditChange}
                    colourOverrides={colourOverrides}
                />
            </I18nextProvider>,
            shadow
        );
    }
}
